(() => {
  if (!customElements.get('mirjo-gift-options')) {
    class MirjoGiftOptions extends HTMLElement {
      connectedCallback() {
        if (this.abortController) return;
        this.abortController = new AbortController();
        this.form = document.getElementById(this.dataset.formId);
        if (!this.form || this.closest('form') !== this.form) return;
        this.toggle = this.querySelector('[data-recipient-toggle]');
        this.fields = this.querySelector('[data-recipient-fields]');
        this.email = this.querySelector('[data-recipient-email]');
        this.date = this.querySelector('[data-recipient-date]');
        this.nameInput = this.querySelector('[data-recipient-name]');
        this.messageInput = this.querySelector('[data-recipient-message]');
        this.paperRadio = this.querySelector('[data-gift-format][value="paper"]');
        this.paperPrice = Number(this.dataset.paperPrice);
        const signal = this.abortController.signal;
        try {
          this.variants = JSON.parse(this.querySelector('[data-gift-variants]').textContent);
        } catch {
          this.variants = {};
        }
        this.querySelector('[data-recipient-offset]').value = String(new Date().getTimezoneOffset());
        const localISODate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        const today = new Date();
        const latest = new Date(today);
        latest.setDate(latest.getDate() + 90);
        this.date.min = localISODate(today);
        this.date.max = localISODate(latest);
        this.addEventListener('input', () => this.update(), { signal });
        this.addEventListener('change', () => this.update(), { signal });
        this.form.addEventListener('change', () => this.update(), { signal });
        this.form.addEventListener('submit', (event) => {
          this.update();
          if (this.paperBusy || this.paperBlocked || !this.form.reportValidity()) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, { capture: true, signal });
        customElements.whenDefined('product-form').then(() => {
          if (!this.isConnected || signal.aborted) return;
          const supported = this.form.dataset.mirjoPaperSupported === 'true';
          this.paperRadio.disabled = !supported || !this.paperAvailable;
          this.querySelector('[data-paper-unavailable]').hidden = !this.paperRadio.disabled;
          this.update();
        });
        this.update();
      }
      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
      }
      update() {
        if (this.paperBusy || this.paperBlocked) return;
        if (this.paperSelected && (!this.paperAvailable || this.paperRadio.disabled)) {
          this.querySelector('[data-gift-format][value="digital"]').checked = true;
        }
        const paper = this.paperSelected;
        const recipientEnabled = this.toggle.checked;
        this.fields.disabled = !paper && !recipientEnabled;
        this.fields.hidden = !paper && !recipientEnabled;
        this.email.disabled = paper;
        this.email.required = !paper && recipientEnabled;
        this.date.disabled = paper;
        this.toggle.disabled = paper;
        this.querySelector('[data-recipient-flag]').disabled = paper;
        this.querySelector('[data-recipient-offset]').disabled = paper;
        this.querySelector('[data-recipient-email-field]').hidden = paper;
        this.querySelector('[data-recipient-date-field]').hidden = paper;
        this.querySelector('[data-digital-recipient-choice]').hidden = paper;
        this.querySelector('[data-digital-self-hint]').hidden = paper;
        this.querySelector('[data-digital-delivery-note]').hidden = paper;
        this.querySelector('[data-paper-note]').hidden = !paper;
        this.toggle.setAttribute('aria-expanded', String(recipientEnabled));
        const motif = this.querySelector('[data-motif-input]:checked');
        if (motif) {
          this.querySelectorAll('[data-preview-card]').forEach((card) => { card.dataset.motif = motif.value; });
          this.querySelectorAll('[data-preview-image]').forEach((image) => { image.src = motif.dataset.image; });
          this.querySelector('[data-preview-title]').textContent = motif.dataset.title;
        }
        const name = paper || recipientEnabled ? this.nameInput.value.replace(/\s+/g, ' ').trim() : '';
        const message = paper || recipientEnabled ? this.messageInput.value.replace(/\s+/g, ' ').trim() : '';
        this.querySelector('[data-preview-name]').textContent = name;
        this.querySelector('[data-preview-message]').textContent = message;
        const variantId = this.form.querySelector('[name="id"]')?.value;
        if (Object.hasOwn(this.variants, variantId)) {
          const variant = this.variants[variantId];
          this.querySelector('[data-preview-value]').textContent = variant.money;
          this.querySelector('[data-preview-total]').textContent = paper ? variant.paper_total : variant.money;
        }
      }
      get paperSelected() {
        return Boolean(this.querySelector('[data-gift-format][value="paper"]:checked'));
      }
      get paperAvailable() {
        return this.dataset.paperAvailable === 'true' && Number(this.dataset.paperVariantId) === 57659788820853 && this.paperPrice === 1000 && this.dataset.currency === 'CHF';
      }
      buildPaperRequest() {
        const variantId = this.form.querySelector('[name="id"]')?.value;
        const variant = this.variants[variantId];
        if (!this.paperAvailable || !variant || ![2500, 5000, 7500, 10000].includes(variant.price)) throw new Error(this.dataset.paperUnavailable);
        const name = this.nameInput.value.replace(/\s+/g, ' ').trim();
        const message = this.messageInput.value.replace(/\s+/g, ' ').trim();
        if (name.length > 80 || message.length > 200) throw new Error(this.dataset.paperError);
        const allowedMotifs = ['birthday', 'for-you', 'thanks', 'christmas', 'wedding', 'for-him', 'birthday-modern', 'mountains'];
        const selectedMotif = this.querySelector('[data-motif-input]:checked')?.value;
        const motif = allowedMotifs.includes(selectedMotif) ? selectedMotif : 'for-you';
        if (!this.paperOrderId) this.paperOrderId = crypto.randomUUID();
        const association = { _MirjoPapierauftrag: this.paperOrderId };
        return {
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                Motiv: motif,
                Kartenname: name,
                Message: message,
                'Recipient email': 'info@mirjo.ch',
                'Recipient name': 'MirJo Druckservice',
                __shopify_send_gift_card_to_recipient: 'true',
                ...association
              }
            },
            {
              id: Number(this.dataset.paperVariantId),
              quantity: 1,
              parent_id: Number(variantId),
              properties: { Gutscheinwert: variant.money, Motiv: motif, Kartenname: name, Grusstext: message, ...association }
            }
          ]
        };
      }
      verifyPaperCart(cart, request) {
        const linked = cart.items?.filter((item) => item.properties?._MirjoPapierauftrag === this.paperOrderId) || [];
        const parent = linked.find((item) => item.gift_card === true && Number(item.variant_id) === request.items[0].id);
        const child = linked.find((item) => Number(item.variant_id) === request.items[1].id);
        return Boolean(linked.length === 2 && parent?.quantity === 1 && child?.quantity === 1 && child.parent_relationship?.parent_key === parent.key);
      }
      async readPaperCart() {
        const response = await fetch(this.dataset.cartDataUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error(this.dataset.paperError);
        return response.json();
      }
      lockPaperControls(productForm) {
        this.querySelectorAll('input, textarea').forEach((input) => { input.disabled = true; });
        productForm.submitButton.disabled = true;
        productForm.submitButton.setAttribute('aria-disabled', 'true');
      }
      showPaperReview(productForm, added) {
        this.paperBlocked = true;
        this.lockPaperControls(productForm);
        this.querySelector('[data-paper-recovery]').hidden = false;
        this.querySelector('[data-paper-status]').textContent = added ? this.dataset.paperAdded : this.dataset.paperError;
        if (!added) productForm.handleErrorMessage(this.dataset.paperError);
      }
      async submitPaper(productForm) {
        if (this.paperBusy || this.paperBlocked) return;
        this.update();
        if (!this.paperSelected || !this.form.reportValidity()) return;
        let request;
        try {
          request = this.buildPaperRequest();
        } catch (error) {
          productForm.handleErrorMessage(error.message || this.dataset.paperUnavailable);
          return;
        }
        this.paperBusy = true;
        this.lockPaperControls(productForm);
        productForm.handleErrorMessage();
        productForm.submitButton.classList.add('loading');
        const spinner = productForm.querySelector('.loading-overlay__spinner');
        spinner?.classList.remove('hidden');
        let added = false;
        try {
          const response = await fetch(this.dataset.cartAddUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(request)
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(this.dataset.paperError);
          added = this.verifyPaperCart(await this.readPaperCart(), request);
          if (!added) throw new Error(this.dataset.paperError);
        } catch {
          try {
            added = this.verifyPaperCart(await this.readPaperCart(), request);
          } catch {
            added = false;
          }
        } finally {
          this.paperBusy = false;
          productForm.submitButton.classList.remove('loading');
          spinner?.classList.add('hidden');
        }
        this.showPaperReview(productForm, added);
        if (added) window.location.assign(this.dataset.cartUrl);
      }
    }
    customElements.define('mirjo-gift-options', MirjoGiftOptions);
  }
  const initIssuedGiftCard = () => {
    document.querySelectorAll('[data-issued-gift]').forEach((page) => {
      if (page.dataset.initialized) return;
      page.dataset.initialized = 'true';
      page.querySelector('[data-gift-print]')?.addEventListener('click', () => window.print());
      const copyButton = page.querySelector('[data-gift-copy]');
      const status = page.querySelector('[data-gift-copy-status]');
      const code = page.querySelector('[data-gift-code]');
      if (copyButton && code && status) {
        copyButton.addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(code.textContent.replace(/\s/g, ''));
            status.textContent = copyButton.dataset.success;
          } catch {
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(code);
            selection.removeAllRanges();
            selection.addRange(range);
            status.textContent = copyButton.dataset.failure;
          }
        });
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initIssuedGiftCard, { once: true });
  else initIssuedGiftCard();
})();


